// src/features/task/form/useTaskForm.ts
// Everything a task form has to know, in one hook.
//
// There are three surfaces — the full create page (/tasks/new), the quick
// drawer over the task list, and the edit page (/tasks/:id/edit) — and they
// must not drift apart. What a task *is* does not change with the size of the
// window it is typed into, nor with whether it already exists: the same
// `ResolveTaskContext` resolution, the same type-driven composition, the same
// defaults, the same validation, the same rows.
//
// `mode` is the only axis of difference, and it is narrow on purpose:
//
//   create — starts empty, keeps a sessionStorage draft, sends CreateTask.
//   edit   — starts from a loaded task, keeps no draft (the values are already
//            saved; there is nothing unwritten to rescue), and sends the diff
//            against what was loaded.
//
// The hook is deliberately state-and-rules only. It never navigates, never
// renders, and never decides what happens after the write — that is the
// caller's question, and the surfaces answer it differently (the create page
// opens the task, the drawer keeps the list in place, the edit page returns to
// the task it was editing).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useI18n,
  useProject,
  useTask,
  useTaskCustomField,
  useTaskTaskPolicy,
  useTaskTaskType,
  useTaskTemplate,
  useTeam,
} from '@react/hooks';

import { attachmentApi } from '@core/api';
import type {
  CreateTaskFromTemplateRequest,
  CustomFieldDefinition,
  CustomFieldValue,
  ResolveTaskContextResponse,
  Task,
  TaskChecklist,
  TaskChecklistItem,
  TaskFormProperty,
  TaskTemplate,
  TaskTypeDefinition,
  UpdateTaskRequest,
} from '@core/types/task';
import type { Actor } from '@core/types/actor';
import type { Entity } from '@core/types/entity';
import type { Project } from '@core/types/project';
import type { Tag } from '@core/types/tag';
import type { Team } from '@core/types/team';

import { useScope } from '@/hooks/useScope';
import {
  assignTagsToEntity,
  buildCreatePayload,
  getDisplayName,
  getEntityId,
  getPriorityColor,
  getPriorityIconPath,
  getStatusMapping,
  loadEntityTags,
  syncTagsForEntity,
  taskPriorityMap,
  validateTaskForm,
} from '@/features/task/logic';
import {
  formPropertyLabelKey,
  resolveFormLayout,
  slotErrorField,
  slotRendersOnCreate,
  type FormPropertySlot,
} from '@/features/task/formProperties';
import { errMsg } from '@/features/task/panelHelpers';
import type { UserPickerScope } from '@/components/UserPicker';

import {
  buildCustomFieldValue,
  buildInitialCustomFields,
  customFieldDraftFromValue,
  customFieldId,
  customFieldLabelFromValue,
  customFieldValuesEqual,
  defaultsForType,
  groupCustomFields,
  hoursFromDuration,
  isCustomFieldEmpty,
  isoToDateInput,
  pickNumberingScheme,
  previewTaskKey,
  resolveCapabilities,
  selectCustomFieldDefinitions,
  selectedTypeIdForTask,
  synthesizeBaseTypeDefinitions,
  taskTypeEnumForDefinition,
  taskTypeIdForRequest,
  typeDefId,
  typeDefLabelKey,
  validateCustomFields,
  type CustomFieldDraftValue,
  type CustomFieldGroup,
  type FieldError,
} from './logic';
import {
  PARENT_KINDS,
  buildScopeChain,
  decorateTemplates,
  parentEntityFor,
  templateTaskTypeId,
  type ParentKind,
  type ParentType,
  type TemplateEntry,
} from './scope';
import {
  clearDraft,
  draftHasContent,
  draftKey,
  loadDraft,
  saveDraft,
  type ChecklistDraft,
  type WatcherDraft,
} from './draft';
import type { PendingFile } from './AttachmentDropzone';

export const EMPTY_TASK_FORM = {
  title: '',
  description: '',
  priority: 'TASK_PRIORITY_MEDIUM',
  assignee: '',
  assigneeName: '',
  due_date: '',
  start_date: '',
  estimated_hours: '',
};

export type TaskFormState = typeof EMPTY_TASK_FORM;

/** Writing a task down, or changing one that already exists. */
export type TaskFormMode = 'create' | 'edit';

/** Section keys for the add-actions row. Custom-field groups add `cf:<group>`. */
export const SECTION_CHECKLISTS = 'checklists';
export const SECTION_WATCHERS = 'watchers';
export const SECTION_ATTACHMENTS = 'attachments';
export const customFieldSectionKey = (group: CustomFieldGroup) =>
  `cf:${group.key || '__ungrouped'}`;

/** Where the form starts: a deep link on the page, the scope in the drawer. */
export interface InitialParent {
  kind: ParentKind;
  id: string;
  /** Entity type name, needed only for the free-form `entity` kind. */
  typeName?: string;
}

export interface UseTaskFormOptions {
  /** Defaults to `create`. */
  mode?: TaskFormMode;
  /** The task being edited. Required in `edit`, ignored in `create`. */
  taskId?: string;
  /** The parent the form opens on. Defaults to "no parent". */
  initialParent?: InitialParent;
  /** A template id from a deep link, applied once the context resolves. */
  templateId?: string | null;
  /** DOM id of the title field — the focus target when the title is invalid. */
  titleFieldId?: string;
  /**
   * What to do once the task exists. The hook has already cleared the draft;
   * the surface decides whether to open the task, or stay where it is.
   */
  onCreated?: (created: Task | null, createdId: string) => void;
  /** What to do once the changes are saved. `edit` only. */
  onSaved?: (taskId: string) => void;
}

/**
 * The parent as the picker and the scope chain understand it. Exposed so a
 * surface can re-point the form at a different container (the drawer follows
 * the workspace scope) without knowing how the chain is rebuilt.
 */
export interface ParentSelection {
  kind: ParentKind;
  id: string;
  typeName: ParentType;
}

/**
 * What the task held when it was loaded — what every save is diffed against,
 * and what "unsaved changes" is measured from.
 *
 * Tags and custom fields are fetched separately and kept separately: folding
 * them into this object would mean writing into a baseline that may not exist
 * yet, and a dropped write reads as an edit nobody made.
 */
interface EditBaseline {
  form: TaskFormState;
  typeId: string;
  categoryId: string;
  watcherIds: string[];
}

/** Every field a saved task fills — a later default must not overwrite one. */
const ALL_TOUCHED = [
  'title',
  'description',
  'type',
  'priority',
  'assignee',
  'due_date',
  'start_date',
  'estimated_hours',
  'tags',
];

/** Tags as a comparable identity — order is not a change. */
const tagKeys = (tags: Tag[]): string =>
  tags
    .map((tag) => tag.entity?.id || tag.name)
    .filter(Boolean)
    .sort()
    .join(' ');

const idKeys = (ids: string[]): string => [...ids].sort().join(' ');

export function useTaskForm(options: UseTaskFormOptions = {}) {
  const {
    mode = 'create',
    taskId = '',
    initialParent,
    templateId: urlTemplateId,
    titleFieldId = 'task-form-title',
    onCreated,
    onSaved,
  } = options;

  const isEdit = mode === 'edit';

  const { t } = useI18n();
  const scope = useScope();

  const store = useTask();
  const templateStore = useTaskTemplate();
  const customFieldStore = useTaskCustomField();
  const taskTypeStore = useTaskTaskType();
  const policyStore = useTaskTaskPolicy();
  const projectStore = useProject();
  const teamStore = useTeam();

  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  /** Fields the person has edited — resolved defaults never overwrite these. */
  const touched = useRef(new Set<string>());
  const restoredKey = useRef<string | null>(null);
  /** Mirrors `isDirty` so effects can read it without depending on it. */
  const dirtyRef = useRef(false);

  // ─── Parent scope ───────────────────────────────────────────────────────
  // The parent decides which types, templates, categories and numbering the
  // rest of this form offers, so changing it re-resolves everything. Its
  // default is "no parent": a deep link, a scoped workspace, or the task being
  // edited may say otherwise, nothing else does.
  const [parentId, setParentId] = useState(initialParent?.id ?? '');
  // `kind` is what the picker offers; `otherTypeName` carries any entity type
  // outside the picked list (deep links, custom scopes) so those round-trip
  // instead of being silently rewritten to "project".
  const [parentKind, setParentKind] = useState<ParentKind>(initialParent?.kind ?? 'none');
  const [otherTypeName, setOtherTypeName] = useState(
    initialParent?.kind === 'entity' ? (initialParent.typeName ?? '') : '',
  );
  /**
   * The entity type name sent to the backend. "No parent" has none — it is the
   * absence of a parent, not a parent of an unnamed type.
   */
  const parentType: ParentType =
    parentKind === 'none' ? '' : parentKind === 'entity' ? otherTypeName.trim() : parentKind;

  // ─── Form ───────────────────────────────────────────────────────────────
  const [form, setForm] = useState<TaskFormState>(EMPTY_TASK_FORM);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [cfValues, setCfValues] = useState<Record<string, CustomFieldDraftValue>>({});
  const [cfLabels, setCfLabels] = useState<Record<string, string>>({});
  const [checklists, setChecklists] = useState<ChecklistDraft[]>([]);
  const [watchers, setWatchers] = useState<WatcherDraft[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  /** Point the form at a container in one move — kind, id and type together. */
  const setParent = useCallback((next: InitialParent) => {
    setParentKind(next.kind);
    setParentId(next.id);
    setOtherTypeName(next.kind === 'entity' ? (next.typeName ?? '') : '');
    setSelectedTemplateId(null);
  }, []);

  // ─── Presentation ───────────────────────────────────────────────────────
  // Sections are open when they hold something; this map records explicit
  // choices on top of that, so collapsing never destroys anything.
  const [sectionOverrides, setSectionOverrides] = useState<Record<string, boolean>>({});
  const [descriptionPreview, setDescriptionPreview] = useState(false);
  const [errors, setErrors] = useState<Record<string, FieldError>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadWarning, setUploadWarning] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  // ─── The task being edited ──────────────────────────────────────────────
  const [loadedTask, setLoadedTask] = useState<Task | null>(null);
  const [taskLoading, setTaskLoading] = useState(isEdit);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<EditBaseline | null>(null);
  /** Null until the matching request answers — nothing to compare against yet. */
  const [baselineTags, setBaselineTags] = useState<Tag[] | null>(null);
  const [baselineCf, setBaselineCf] = useState<Record<string, CustomFieldDraftValue> | null>(null);
  /** Prefill happens once per task — a later render must not undo an edit. */
  const prefilled = useRef(false);
  const typeResolved = useRef(false);

  // ─── ResolveTaskContext ─────────────────────────────────────────────────
  const [context, setContext] = useState<ResolveTaskContextResponse | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState<string | null>(null);
  const [contextNonce, setContextNonce] = useState(0);
  const retryContext = useCallback(() => setContextNonce((n) => n + 1), []);

  // The records behind the parent picker. Their own `parent` is the next link
  // in the scope chain, so a project inside a team inherits the team's
  // templates and policy.
  const projects = (projectStore.items ?? []) as Project[];
  const teams = (teamStore.items ?? []) as Team[];

  const parentRecord = useMemo<Project | Team | null>(() => {
    if (!parentId) return null;
    if (parentKind === 'project') return projects.find((p) => getEntityId(p) === parentId) ?? null;
    if (parentKind === 'team') return teams.find((team) => getEntityId(team) === parentId) ?? null;
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, parentKind, projectStore.items, teamStore.items]);

  /** True when a real container was picked — "none" and "tenant" are not one. */
  const hasContainerParent =
    parentKind !== 'none' && parentKind !== 'tenant' && !!parentId && !!parentType;

  const { scopeEntity, chain } = useMemo(
    () =>
      buildScopeChain(
        scope,
        hasContainerParent
          ? { id: parentId, typeName: parentType, parent: parentRecord?.parent }
          : null,
      ),
    [scope, hasContainerParent, parentId, parentType, parentRecord],
  );
  const chainIds = useMemo(() => chain.map((entity) => entity.id).filter(Boolean), [chain]);

  const loadContext = useCallback(async () => {
    setContextLoading(true);
    setContextError(null);
    try {
      const response = await policyStore.ResolveTaskContext({
        // The scope the *answer* is resolved against. With no parent this is
        // the tenant — which is not the same as filing the task under it.
        scope_entity: scopeEntity,
        // Most-specific → least-specific. The server walks this to merge
        // policy and to collect templates; a single link would silently drop
        // everything inherited from the parent's own parent and the tenant.
        scope_chain: chain,
        task_type_id: selectedTypeId || undefined,
      });
      setContext(response);
    } catch (e) {
      setContext(null);
      setContextError(errMsg(e, t('task.createForm.contextError')));
    } finally {
      setContextLoading(false);
    }
    // `selectedTypeId` is intentionally not a dependency: re-resolving on every
    // type click would churn the list the person is clicking through.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEntity, chain, policyStore, t]);

  useEffect(() => {
    void loadContext();
  }, [loadContext, contextNonce]);

  // Supporting catalogues. Each is a fallback for something the context call
  // may not carry on an unconfigured install.
  const loadProjects = useCallback(() => {
    void projectStore.ListProjects({
      pagination: { limit: 100, cursor: '', direction: 'forward' },
      sort: [],
    } as never);
  }, [projectStore]);

  const loadTeams = useCallback(() => {
    void teamStore.ListTeams({
      pagination: { limit: 100, cursor: '', direction: 'forward' },
      sort: [],
    } as never);
  }, [teamStore]);

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadProjects, scope.productId]);

  useEffect(() => {
    loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTeams, scope.productId]);

  useEffect(() => {
    void customFieldStore.ListCustomFieldDefinitions({
      pagination: { limit: 200, cursor: '', direction: 'forward' },
      sort: [],
    } as never);
  }, [customFieldStore]);

  useEffect(() => {
    void taskTypeStore.ListTaskTypes({
      pagination: { limit: 100, cursor: '', direction: 'forward' },
      sort: [],
    } as never);
  }, [taskTypeStore]);

  // ─── Derived: types, policy, capabilities ───────────────────────────────
  const availableTypes: TaskTypeDefinition[] = useMemo(() => {
    const fromContext = context?.allowed_types ?? [];
    if (fromContext.length > 0) return fromContext.filter((d) => d.is_active !== false);
    const fromStore = (taskTypeStore.items ?? []) as TaskTypeDefinition[];
    if (fromStore.length > 0) return fromStore.filter((d) => d.is_active !== false);
    return synthesizeBaseTypeDefinitions();
  }, [context, taskTypeStore.items]);

  const policy = context?.effective_policy ?? null;

  const selectedType = useMemo(
    () => availableTypes.find((d) => typeDefId(d) === selectedTypeId) ?? availableTypes[0] ?? null,
    [availableTypes, selectedTypeId],
  );

  const capabilities = useMemo(
    () => resolveCapabilities(selectedType, policy),
    [selectedType, policy],
  );

  const categories = context?.available_categories ?? [];

  // First selection: the policy's default type, else the first allowed type.
  // A task being edited brings its own and must never be handed a default.
  useEffect(() => {
    if (isEdit || selectedTypeId || availableTypes.length === 0) return;
    const preferred =
      availableTypes.find((d) => typeDefId(d) === policy?.default_task_type_id) ?? availableTypes[0];
    setSelectedTypeId(typeDefId(preferred));
  }, [availableTypes, policy, selectedTypeId, isEdit]);

  // ─── Loading the task being edited ──────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !taskId) return;
    let cancelled = false;
    prefilled.current = false;
    typeResolved.current = false;
    setTaskLoading(true);
    setTaskError(null);
    void (async () => {
      try {
        const task = await store.GetTask(taskId);
        if (!cancelled) setLoadedTask((task ?? null) as Task | null);
      } catch (e) {
        if (!cancelled) setTaskError(errMsg(e, t('task.form.loadError')));
      } finally {
        if (!cancelled) setTaskLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      store.clearCurrent();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, taskId, scope.productId]);

  // The task's own values. Everything they fill is marked touched: these are
  // answers somebody already gave, and a type default arriving a moment later
  // must not quietly replace one.
  useEffect(() => {
    if (!isEdit || !loadedTask || prefilled.current) return;
    prefilled.current = true;

    const next: TaskFormState = {
      title: loadedTask.title ?? '',
      description: loadedTask.description ?? '',
      priority: (loadedTask.priority as string) || EMPTY_TASK_FORM.priority,
      assignee: loadedTask.assignee?.entity?.id ?? '',
      assigneeName: loadedTask.assignee ? getDisplayName(loadedTask.assignee) : '',
      due_date: isoToDateInput(loadedTask.due_date as string | undefined),
      start_date: isoToDateInput(loadedTask.start_date as string | undefined),
      estimated_hours: hoursFromDuration(loadedTask.estimated_duration as string | undefined),
    };
    setForm(next);
    setCategoryId(loadedTask.category_id || '');

    const parent = loadedTask.parent;
    if (parent?.id) {
      const typeName = parent.type_name || '';
      const known = typeName !== 'none' && (PARENT_KINDS as readonly string[]).includes(typeName);
      setParentKind(known ? (typeName as ParentKind) : 'entity');
      setOtherTypeName(known ? '' : typeName);
      setParentId(parent.id);
    }

    const loadedWatchers: WatcherDraft[] = (loadedTask.watchers?.actors ?? [])
      .map((actor) => ({ id: actor.entity?.id || '', name: getDisplayName(actor) }))
      .filter((watcher) => watcher.id);
    setWatchers(loadedWatchers);

    touched.current = new Set(ALL_TOUCHED);
    setBaseline({
      form: next,
      typeId: '',
      categoryId: loadedTask.category_id || '',
      watcherIds: loadedWatchers.map((w) => w.id),
    });
  }, [isEdit, loadedTask]);

  // The type list arrives on its own schedule, so the task's type is resolved
  // against it separately — and once, so a later refresh cannot undo a change.
  useEffect(() => {
    if (!isEdit || !loadedTask || typeResolved.current || availableTypes.length === 0) return;
    typeResolved.current = true;
    const resolved = selectedTypeIdForTask(availableTypes, loadedTask);
    if (!resolved) return;
    setSelectedTypeId(resolved);
    setBaseline((prev) => (prev ? { ...prev, typeId: resolved } : prev));
  }, [isEdit, loadedTask, availableTypes]);

  useEffect(() => {
    if (!isEdit || !taskId) return;
    let cancelled = false;
    void loadEntityTags(taskId, 'task').then((loaded) => {
      if (cancelled) return;
      setTags(loaded);
      setBaselineTags(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [isEdit, taskId]);

  // Stored custom field values, read back through the inverse of the mapping
  // that wrote them, so editing shows exactly what saving produced.
  useEffect(() => {
    if (!isEdit || !taskId) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await store.GetTaskCustomFields(taskId);
        if (cancelled) return;
        const stored = (response?.values?.values ?? []) as CustomFieldValue[];
        const definitions = (response?.definitions?.definitions ?? []) as CustomFieldDefinition[];
        const byId = new Map(definitions.map((def) => [customFieldId(def), def]));
        const values: Record<string, CustomFieldDraftValue> = {};
        const labels: Record<string, string> = {};
        for (const value of stored) {
          const key = value.field_id || value.field_slug;
          if (!key) continue;
          // The value carries its own `field_type`, which is the fallback when
          // this scope's definition list does not contain the field.
          const def =
            byId.get(value.field_id) ??
            definitions.find((d) => d.slug === value.field_slug) ??
            ({ field_type: value.field_type } as CustomFieldDefinition);
          values[key] = customFieldDraftFromValue(def, value);
          const label = customFieldLabelFromValue(value);
          if (label) labels[key] = label;
        }
        setCfValues(values);
        setCfLabels(labels);
        setBaselineCf(values);
      } catch {
        // A task with no custom fields configured is not an error surface.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, taskId]);

  // ─── Draft ──────────────────────────────────────────────────────────────
  // Create only. A draft exists to rescue what was never written down; an
  // existing task's values are already saved, so mirroring them into
  // sessionStorage would only risk resurrecting them over newer ones.
  const storageKey = useMemo(
    () => draftKey(scope.productId, parentId),
    [scope.productId, parentId],
  );

  useEffect(() => {
    if (isEdit) return;
    if (restoredKey.current === storageKey) return;
    restoredKey.current = storageKey;
    // Switching the parent mid-compose changes the draft key. Whatever is
    // already on screen is newer than any stored draft, so it wins.
    if (dirtyRef.current) return;
    const draft = loadDraft(storageKey);
    if (!draftHasContent(draft) || !draft) {
      setDraftRestored(false);
      return;
    }
    setForm({
      title: draft.title,
      description: draft.description,
      priority: draft.priority || EMPTY_TASK_FORM.priority,
      assignee: draft.assignee,
      assigneeName: draft.assigneeName,
      due_date: draft.dueDate,
      start_date: draft.startDate,
      estimated_hours: draft.estimatedHours,
    });
    if (draft.typeId) setSelectedTypeId(draft.typeId);
    if (draft.parentKind) {
      const known = (PARENT_KINDS as readonly string[]).includes(draft.parentKind);
      setParentKind(known ? (draft.parentKind as ParentKind) : 'entity');
      // A free-form parent type lives in `parentType`; only the named kinds
      // carry no type name of their own.
      setOtherTypeName(
        !known
          ? draft.parentType || draft.parentKind
          : draft.parentKind === 'entity'
            ? draft.parentType || ''
            : '',
      );
    }
    setCategoryId(draft.categoryId);
    setSelectedTemplateId(draft.templateId);
    setTags(draft.tags || []);
    setCfValues(draft.customFieldValues || {});
    setCfLabels(draft.customFieldLabels || {});
    setChecklists(draft.checklists || []);
    setWatchers(draft.watchers || []);
    // A restored draft is the person's own work — never re-apply defaults over
    // it. Sections holding restored content open themselves, because openness
    // follows content.
    touched.current = new Set(['priority', 'assignee', 'estimated_hours', 'tags', 'type']);
    setDraftRestored(true);
  }, [storageKey, isEdit]);

  const checklistItemCount = checklists.reduce(
    (total, cl) => total + cl.items.filter((item) => item.content.trim()).length,
    0,
  );

  /**
   * Whether there is anything to lose. In `create` that is "the form holds
   * something"; in `edit` it is "the form differs from what was loaded" — an
   * untouched edit form is full of values, and none of them are unsaved.
   */
  const isDirty = isEdit
    ? !!baseline &&
      (form.title !== baseline.form.title ||
        form.description !== baseline.form.description ||
        form.priority !== baseline.form.priority ||
        form.assignee !== baseline.form.assignee ||
        form.due_date !== baseline.form.due_date ||
        form.start_date !== baseline.form.start_date ||
        form.estimated_hours !== baseline.form.estimated_hours ||
        selectedTypeId !== baseline.typeId ||
        categoryId !== baseline.categoryId ||
        (!!baselineTags && tagKeys(tags) !== tagKeys(baselineTags)) ||
        idKeys(watchers.map((w) => w.id)) !== idKeys(baseline.watcherIds) ||
        checklistItemCount > 0 ||
        (!!baselineCf &&
          Object.keys({ ...baselineCf, ...cfValues }).some(
            (key) => !customFieldValuesEqual(cfValues[key], baselineCf[key]),
          )))
    : form.title.trim() !== '' ||
      form.description.trim() !== '' ||
      tags.length > 0 ||
      watchers.length > 0 ||
      pendingFiles.length > 0 ||
      checklists.some((cl) => cl.items.some((it) => it.content.trim())) ||
      Object.values(cfValues).some((v) =>
        Array.isArray(v) ? v.length > 0 : String(v ?? '').trim(),
      );
  dirtyRef.current = isDirty;

  /** The draft as it stands right now — the shape sessionStorage holds. */
  const draftSnapshot = useCallback(
    () => ({
      title: form.title,
      description: form.description,
      typeId: selectedTypeId,
      categoryId,
      priority: form.priority,
      assignee: form.assignee,
      assigneeName: form.assigneeName,
      dueDate: form.due_date,
      startDate: form.start_date,
      estimatedHours: form.estimated_hours,
      parentId,
      parentKind,
      parentType,
      templateId: selectedTemplateId,
      tags,
      customFieldValues: cfValues,
      customFieldLabels: cfLabels,
      checklists,
      watchers,
    }),
    [
      form,
      selectedTypeId,
      categoryId,
      parentId,
      parentKind,
      parentType,
      selectedTemplateId,
      tags,
      cfValues,
      cfLabels,
      checklists,
      watchers,
    ],
  );

  useEffect(() => {
    if (isEdit || !isDirty) return;
    const handle = window.setTimeout(() => saveDraft(storageKey, draftSnapshot()), 400);
    return () => window.clearTimeout(handle);
  }, [isEdit, isDirty, storageKey, draftSnapshot]);

  /**
   * Write the draft now rather than in 400ms. Handing the work to the other
   * surface (drawer → full page) happens on a click, and a click is faster
   * than the debounce.
   */
  const flushDraft = useCallback(() => {
    if (isEdit || !dirtyRef.current) return;
    saveDraft(storageKey, draftSnapshot());
  }, [isEdit, storageKey, draftSnapshot]);

  const discardDraft = useCallback(() => {
    clearDraft(storageKey);
    setForm(EMPTY_TASK_FORM);
    setTags([]);
    setCfValues({});
    setCfLabels({});
    setChecklists([]);
    setWatchers([]);
    setPendingFiles([]);
    setSelectedTemplateId(null);
    setCategoryId('');
    setErrors({});
    setSectionOverrides({});
    setDraftRestored(false);
    setUploadWarning(false);
    touched.current = new Set();
  }, [storageKey]);

  // Closing the tab is the one exit no in-page dialog can intercept. In
  // `create` the draft already covers it; in `edit` there is no draft, so the
  // browser's own prompt is what stands between an edit and losing it.
  useEffect(() => {
    if (!isEdit || !isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isEdit, isDirty]);

  // ─── Defaults: category → policy (container merged) → type ──────────────
  // One pass, one precedence order. The category is the most specific answer,
  // the effective policy already carries whatever the container contributed,
  // and the type definition is the base. Anything the person touched is theirs
  // — and everything on a loaded task counts as touched.
  const selectedCategory = categories.find((c) => getEntityId(c) === categoryId) ?? null;

  useEffect(() => {
    const typeDefaults = defaultsForType(selectedType);

    if (!touched.current.has('type')) {
      const defaultTypeId = selectedCategory?.default_task_type_id || policy?.default_task_type_id;
      if (defaultTypeId && availableTypes.some((d) => typeDefId(d) === defaultTypeId)) {
        setSelectedTypeId(defaultTypeId);
      }
    }

    const priority =
      (selectedCategory?.default_priority as string | undefined) ||
      (policy?.default_priority as string | undefined) ||
      typeDefaults.priority;
    const assigneeId = policy?.default_assignee?.entity?.id || typeDefaults.assigneeId;
    const assigneeName = policy?.default_assignee?.display_name || typeDefaults.assigneeName;

    setForm((prev) => ({
      ...prev,
      priority: touched.current.has('priority') ? prev.priority : priority || prev.priority,
      assignee: touched.current.has('assignee') ? prev.assignee : assigneeId || prev.assignee,
      assigneeName: touched.current.has('assignee')
        ? prev.assigneeName
        : assigneeName || prev.assigneeName,
      estimated_hours: touched.current.has('estimated_hours')
        ? prev.estimated_hours
        : typeDefaults.estimatedHours || prev.estimated_hours,
    }));

    if (!touched.current.has('tags') && typeDefaults.tags.length > 0) {
      setTags((prev) => {
        const existing = new Set(prev.map((tag) => tag.name));
        const added = typeDefaults.tags
          .filter((name) => !existing.has(name))
          .map((name) => ({ name }) as Tag);
        return added.length ? [...prev, ...added] : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, policy, selectedCategory]);

  // ─── Templates ──────────────────────────────────────────────────────────
  // `ResolveTaskContext.available_templates` is the single source of truth: it
  // is already the union over the parent's scope chain, already ordered
  // most-specific-first, and already filtered to the selected task type.
  const templateEntries: TemplateEntry[] = useMemo(() => {
    const fromContext = (context?.available_templates ?? []).filter(
      (tpl) => tpl.is_active !== false,
    );
    return decorateTemplates(fromContext, parentId, chainIds);
  }, [context, parentId, chainIds]);

  const applyTemplate = useCallback(
    (template: TaskTemplate | null) => {
      if (!template) {
        setSelectedTemplateId(null);
        return;
      }
      setSelectedTemplateId(getEntityId(template) || null);
      // A template may declare the task type it belongs to; honour it over the
      // legacy `default_type` enum when present.
      const boundTypeId = templateTaskTypeId(template);
      if (boundTypeId) setSelectedTypeId(boundTypeId);
      setForm((prev) => ({
        ...prev,
        title: prev.title || template.default_title || '',
        description: prev.description || template.default_description || '',
        priority: template.default_priority || prev.priority,
      }));
      if (!boundTypeId && template.default_type) {
        const match = availableTypes.find(
          (d) => taskTypeEnumForDefinition(d) === template.default_type,
        );
        if (match) setSelectedTypeId(typeDefId(match));
      }
      const templateChecklists = template.default_checklists?.checklists ?? [];
      if (templateChecklists.length > 0) {
        // The checklist section opens itself: it now holds content.
        setChecklists(
          templateChecklists.map((cl: TaskChecklist) => ({
            title: cl.title || t('task.checklist'),
            items: ((cl.items as { items?: TaskChecklistItem[] } | undefined)?.items ?? []).map(
              (item) => ({
                id: crypto.randomUUID(),
                content: item.content || '',
                is_done: false,
              }),
            ),
          })),
        );
      }
    },
    [availableTypes, t],
  );

  // A template id in the URL is a deliberate entry point. Prefer the resolved
  // list; if the link points outside this parent's scope, fetch that one
  // template by id rather than listing templates the parent does not own.
  const appliedUrlTemplate = useRef(false);
  useEffect(() => {
    if (isEdit || !urlTemplateId || appliedUrlTemplate.current || contextLoading) return;
    const known = templateEntries.find((entry) => entry.id === urlTemplateId);
    if (known) {
      appliedUrlTemplate.current = true;
      applyTemplate(known.template);
      return;
    }
    appliedUrlTemplate.current = true;
    templateStore
      .GetTemplate(urlTemplateId)
      .then((template) => applyTemplate(template))
      .catch(() => {
        /* A dead template link must not block creating the task. */
      });
  }, [isEdit, urlTemplateId, templateEntries, contextLoading, applyTemplate, templateStore]);

  // ─── Numbering preview ──────────────────────────────────────────────────
  const numberingScheme = useMemo(
    () =>
      pickNumberingScheme(context?.available_numbering_schemes, {
        typeId: selectedTypeId,
        categoryId,
      }),
    [context, selectedTypeId, categoryId],
  );
  // A key is assigned once, at creation. Previewing one over a task that
  // already has its own would be a guess presented as a fact.
  const keyPreview = isEdit ? '' : previewTaskKey(numberingScheme);

  // ─── Form layout ────────────────────────────────────────────────────────
  // Which properties this type's forms offer, in which order, and which of them
  // must be answered. An empty configuration is the platform default set, so
  // this is never "no properties" — see resolveFormLayout.
  const formLayout = useMemo(() => resolveFormLayout(selectedType), [selectedType]);

  /** The slots a *creation* form has a control for. */
  const formSlots = useMemo(() => formLayout.slots.filter(slotRendersOnCreate), [formLayout]);

  const shownProperties = useMemo(
    () => new Set(formSlots.map((slot) => slot.property).filter(Boolean) as TaskFormProperty[]),
    [formSlots],
  );

  const showsProperty = useCallback(
    (property: TaskFormProperty): boolean => shownProperties.has(property),
    [shownProperties],
  );

  /**
   * True when the type configured a form and left this property out of it.
   * Only then is a value the person typed refused a place in the payload — a
   * type saying nothing means the default set, not "send nothing".
   */
  const hidesProperty = useCallback(
    (property: TaskFormProperty): boolean => formLayout.configured && !shownProperties.has(property),
    [formLayout.configured, shownProperties],
  );

  // ─── Custom fields ──────────────────────────────────────────────────────
  const allCustomFields = useMemo(
    () => (customFieldStore.items ?? []) as CustomFieldDefinition[],
    [customFieldStore.items],
  );

  /**
   * Custom fields the type pulled into the property panel. Naming one in
   * `form_properties` is itself a statement that the type offers it, so these
   * are not narrowed by `custom_field_ids` — that list answers the different
   * question of which fields a task of this type may carry at all.
   *
   * A slot marked required makes the field required here regardless of the
   * definition's own flag: the type is stating what *its* form asks for.
   */
  const panelCustomFields = useMemo(() => {
    if (!capabilities.customFields) return [];
    const entries: { slot: FormPropertySlot; def: CustomFieldDefinition }[] = [];
    for (const slot of formSlots) {
      if (!slot.customFieldId) continue;
      const found = allCustomFields.find(
        (def) => customFieldId(def) === slot.customFieldId || def.slug === slot.customFieldId,
      );
      if (!found) continue;
      entries.push({ slot, def: slot.required ? { ...found, is_required: true } : found });
    }
    return entries;
  }, [capabilities.customFields, formSlots, allCustomFields]);

  const panelCustomFieldIds = useMemo(
    () => new Set(panelCustomFields.map((entry) => customFieldId(entry.def))),
    [panelCustomFields],
  );

  /**
   * The rest, offered behind the add-actions as before. A field that moved into
   * the panel is taken out of here rather than repeated: two homes for one field
   * means two places to look and two places to type, and the panel is the home
   * the type asked for.
   */
  const sectionCustomFields = useMemo(() => {
    if (!capabilities.customFields) return [];
    return selectCustomFieldDefinitions(allCustomFields, selectedType, policy).filter(
      (def) => !panelCustomFieldIds.has(customFieldId(def)),
    );
  }, [capabilities.customFields, allCustomFields, selectedType, policy, panelCustomFieldIds]);

  /** Everything on screen — what validation runs over and the payload carries. */
  const visibleCustomFields = useMemo(
    () => [...panelCustomFields.map((entry) => entry.def), ...sectionCustomFields],
    [panelCustomFields, sectionCustomFields],
  );

  const customFieldGroups = useMemo(
    () => groupCustomFields(sectionCustomFields),
    [sectionCustomFields],
  );

  // ─── Required properties ────────────────────────────────────────────────
  /**
   * Whether a property the type marked required is still unanswered. Type and
   * priority are absent on purpose: both always carry a value, so requiring
   * them is a condition that is met the moment the form opens.
   */
  const propertyUnanswered = useCallback(
    (property: TaskFormProperty): boolean => {
      switch (property) {
        case 'TASK_FORM_PROPERTY_PARENT':
          // "No parent" is a chosen answer everywhere else in this form, but a
          // type that requires a parent is asking for a container, and the
          // absence of one is not a container.
          return parentKind === 'none' || (parentKind !== 'tenant' && !parentId);
        case 'TASK_FORM_PROPERTY_CATEGORY':
          return !categoryId;
        case 'TASK_FORM_PROPERTY_ASSIGNEE':
          return !form.assignee;
        case 'TASK_FORM_PROPERTY_DUE_DATE':
          return !form.due_date;
        case 'TASK_FORM_PROPERTY_START_DATE':
          return !form.start_date;
        case 'TASK_FORM_PROPERTY_ESTIMATE':
          return !form.estimated_hours.trim();
        case 'TASK_FORM_PROPERTY_TAGS':
          return tags.length === 0;
        case 'TASK_FORM_PROPERTY_WATCHERS':
          return watchers.length === 0;
        default:
          return false;
      }
    },
    [parentKind, parentId, categoryId, form, tags, watchers],
  );

  /** Required built-in properties still unanswered, named for the message. */
  const missingProperties = useMemo(() => {
    const out: { field: string; label: string }[] = [];
    for (const slot of formSlots) {
      if (!slot.required || !slot.property) continue;
      if (!propertyUnanswered(slot.property)) continue;
      out.push({ field: slotErrorField(slot), label: t(formPropertyLabelKey(slot.property)) });
    }
    return out;
  }, [formSlots, propertyUnanswered, t]);

  // ─── Sections ───────────────────────────────────────────────────────────
  /** Whether the type's `preset` says this person wants everything open. */
  const advancedPreset = selectedType?.preset === 'TASK_TYPE_PRESET_ADVANCED';

  const filledInGroup = useCallback(
    (group: CustomFieldGroup): number =>
      group.fields.filter((def) => !isCustomFieldEmpty(cfValues[customFieldId(def)])).length,
    [cfValues],
  );

  /** A section is open when it holds content, unless explicitly toggled. */
  const sectionOpen = useCallback(
    (key: string, holdsContent: boolean): boolean => sectionOverrides[key] ?? holdsContent,
    [sectionOverrides],
  );

  const toggleSection = useCallback(
    (key: string, holdsContent: boolean) =>
      setSectionOverrides((prev) => ({ ...prev, [key]: !(prev[key] ?? holdsContent) })),
    [],
  );

  // ─── Handlers ───────────────────────────────────────────────────────────
  const setField = useCallback(
    (field: keyof TaskFormState, value: string) => {
      touched.current.add(field);
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    [],
  );

  const setAssignee = useCallback((userId: string, displayName: string) => {
    touched.current.add('assignee');
    setForm((prev) => ({ ...prev, assignee: userId, assigneeName: displayName }));
    setErrors((prev) => {
      if (!prev.assignee) return prev;
      const next = { ...prev };
      delete next.assignee;
      return next;
    });
  }, []);

  const setTagsTouched = useCallback((next: Tag[]) => {
    touched.current.add('tags');
    setTags(next);
  }, []);

  // Tracked like the other properties so a category chosen by hand can be told
  // apart from one a default put there — only the first is worth warning about
  // when the type turns out not to ask for it.
  const setCategoryTouched = useCallback((next: string) => {
    touched.current.add('category');
    setCategoryId(next);
  }, []);

  const setCustomField = useCallback(
    (fieldId: string, value: CustomFieldDraftValue, label?: string) => {
      setCfValues((prev) => ({ ...prev, [fieldId]: value }));
      if (label !== undefined) setCfLabels((prev) => ({ ...prev, [fieldId]: label }));
      setErrors((prev) => {
        if (!prev[fieldId]) return prev;
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    },
    [],
  );

  const handleAddFiles = useCallback((incoming: FileList | File[]) => {
    const added: PendingFile[] = Array.from(incoming).map((file) => ({
      id: crypto.randomUUID(),
      file,
    }));
    setPendingFiles((prev) => [...prev, ...added]);
    for (const item of added) {
      if (!item.file.type.startsWith('image/')) continue;
      const reader = new FileReader();
      reader.onload = () => {
        setPendingFiles((prev) =>
          prev.map((f) => (f.id === item.id ? { ...f, previewUrl: String(reader.result) } : f)),
        );
      };
      reader.readAsDataURL(item.file);
    }
  }, []);

  const uploadFiles = async (): Promise<{ ids: string[]; failed: number }> => {
    const ids: string[] = [];
    let failed = 0;
    for (const pending of pendingFiles) {
      try {
        const urlResponse = (await attachmentApi.GetUploadUrl({
          file_name: pending.file.name,
          mime_type: pending.file.type || 'application/octet-stream',
          size_bytes: pending.file.size,
        } as never)) as { upload_url?: { url?: string }; upload_id?: string };
        const uploadUrl = urlResponse?.upload_url?.url;
        const uploadId = urlResponse?.upload_id;
        if (!uploadUrl || !uploadId) {
          failed++;
          continue;
        }
        const putResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: pending.file,
          headers: { 'Content-Type': pending.file.type || 'application/octet-stream' },
        });
        if (!putResponse.ok) {
          failed++;
          continue;
        }
        const confirmed = (await attachmentApi.ConfirmUpload({ upload_id: uploadId } as never)) as {
          entity?: { id?: string };
          attachment?: { entity?: { id?: string } };
        };
        const attachmentId = confirmed?.entity?.id || confirmed?.attachment?.entity?.id;
        if (attachmentId) ids.push(attachmentId);
        else failed++;
      } catch {
        failed++;
      }
    }
    return { ids, failed };
  };

  const focusFirstError = (nextErrors: Record<string, FieldError>) => {
    window.requestAnimationFrame(() => {
      if (nextErrors.title) {
        document.getElementById(titleFieldId)?.focus();
        return;
      }
      const anchor = formRef.current?.querySelector<HTMLElement>('[data-field-error="true"]');
      if (!anchor) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      anchor.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      // A pill *is* the control; a field wrapper *contains* one.
      const focusable = 'input, textarea, button, [tabindex]:not([tabindex="-1"])';
      const target = anchor.matches(focusable)
        ? anchor
        : anchor.querySelector<HTMLElement>(focusable);
      target?.focus();
    });
  };

  /**
   * The same rules in both modes: what makes a task invalid does not depend on
   * whether it exists yet. True when the form may be sent.
   */
  const validate = (): boolean => {
    const nextErrors: Record<string, FieldError> = {};

    for (const [field, key] of Object.entries(validateTaskForm(form))) {
      nextErrors[field] = { key, params: { field: t('task.field.title'), min: 2, max: 200 } };
    }
    if (capabilities.requiresAssignee && !form.assignee) {
      nextErrors.assignee = { key: 'task.assigneeRequired' };
    }
    // What the type said its form must collect. The service stores the flag but
    // does not reject tasks over it — the same arrangement `requires_assignee`
    // already has — so the form that renders it is the one that enforces it.
    for (const missing of missingProperties) {
      nextErrors[missing.field] = { key: 'validation.required', params: { field: missing.label } };
    }
    if (form.start_date && form.due_date && form.start_date > form.due_date) {
      nextErrors.due_date = { key: 'task.createForm.error.dateOrder' };
    }
    Object.assign(nextErrors, validateCustomFields(visibleCustomFields, cfValues));

    if (Object.keys(nextErrors).length === 0) {
      setErrors({});
      return true;
    }

    setErrors(nextErrors);
    // An invalid custom field may sit inside a collapsed section — open it,
    // otherwise the message would be announced about something unseen.
    const reopen: Record<string, boolean> = {};
    for (const group of customFieldGroups) {
      if (group.fields.some((def) => nextErrors[customFieldId(def)])) {
        reopen[customFieldSectionKey(group)] = true;
      }
    }
    if (Object.keys(reopen).length > 0) {
      setSectionOverrides((prev) => ({ ...prev, ...reopen }));
    }
    focusFirstError(nextErrors);
    return false;
  };

  /** The checklist drafts worth sending — a title, or at least one written item. */
  const meaningfulChecklists = () =>
    checklists
      .map((cl) => ({
        title: cl.title,
        items: {
          items: cl.items
            .filter((item) => item.content.trim())
            .map((item, index) => ({
              content: item.content,
              is_done: item.is_done,
              order: index,
            })),
        },
      }))
      .filter((cl) => cl.items.items.length > 0 || cl.title.trim());

  const submitCreate = async () => {
    setUploadWarning(false);
    setIsSubmitting(true);

    // "No parent" sends no parent at all; "the whole tenant" sends the tenant.
    // The parent survives a type that hides its row: it is where the task is
    // filed, and it comes from the link that was followed or the workspace that
    // is open, not from something typed into the panel. Hiding the row means
    // "do not ask", never "file it somewhere else".
    const parentEntity = parentEntityFor(scope, parentKind, parentId, parentType);

    /** Tags a type that hides them would never have collected. */
    const sendTags = hidesProperty('TASK_FORM_PROPERTY_TAGS') ? [] : tags;

    try {
      // Template path — the server expands the template (resources, workflow
      // hooks); only these four fields are overridable per the RPC contract.
      if (selectedTemplateId) {
        const overrides: CreateTaskFromTemplateRequest = {
          title_override: form.title.trim() || undefined,
          parent: parentEntity ?? undefined,
          assignee:
            form.assignee && !hidesProperty('TASK_FORM_PROPERTY_ASSIGNEE')
              ? { entity: { id: form.assignee }, display_name: form.assigneeName || '' }
              : undefined,
          due_date:
            form.due_date && !hidesProperty('TASK_FORM_PROPERTY_DUE_DATE')
              ? new Date(form.due_date).toISOString()
              : undefined,
        } as CreateTaskFromTemplateRequest;
        const created = await store.CreateTaskFromTemplate(selectedTemplateId, overrides);
        const createdId = getEntityId(created);
        if (createdId && sendTags.length > 0) await assignTagsToEntity(sendTags, createdId, 'task');
        clearDraft(storageKey);
        onCreated?.((created ?? null) as Task | null, createdId);
        return;
      }

      const payload = buildCreatePayload({
        title: form.title,
        description: form.description,
        type: taskTypeEnumForDefinition(selectedType),
        priority: form.priority,
        assignee: form.assignee,
        assignee_name: form.assigneeName,
        due_date: form.due_date,
      });

      if (parentEntity) payload.parent = parentEntity;

      // A property the type left out of its form is a property it does not ask
      // for, so whatever a previous type collected under it does not travel.
      // The person is told which values that is, next to Create — silence here
      // would be the form losing work without admitting it.
      if (hidesProperty('TASK_FORM_PROPERTY_PRIORITY')) delete payload.priority;
      if (hidesProperty('TASK_FORM_PROPERTY_ASSIGNEE')) delete payload.assignee;
      if (hidesProperty('TASK_FORM_PROPERTY_DUE_DATE')) delete payload.due_date;

      // The base enum says "a task"; the registry id says *which* task type,
      // and with it the statuses, transitions, templates and numbering.
      const taskTypeId = taskTypeIdForRequest(selectedType);
      if (taskTypeId) payload.task_type_id = taskTypeId;
      if (categoryId && !hidesProperty('TASK_FORM_PROPERTY_CATEGORY')) {
        payload.category_id = categoryId;
      }

      if (form.start_date && !hidesProperty('TASK_FORM_PROPERTY_START_DATE')) {
        payload.start_date = new Date(form.start_date).toISOString();
      }
      // Gated on what the type asks for, not only on the value: a hidden row
      // still holds whatever was typed before the type changed, and sending it
      // would be exactly the silent save the type warning promises not to do.
      const hours = parseFloat(form.estimated_hours);
      if (
        form.estimated_hours &&
        !Number.isNaN(hours) &&
        hours > 0 &&
        !hidesProperty('TASK_FORM_PROPERTY_ESTIMATE')
      ) {
        payload.estimated_duration = `${Math.round(hours * 3600)}s`;
      }
      if (sendTags.length > 0) {
        payload.tags = sendTags.map((tag) => tag.name || tag.entity?.id).filter(Boolean);
      }

      if (pendingFiles.length > 0) {
        const { ids, failed } = await uploadFiles();
        if (ids.length > 0) payload.attachments = { entities: ids.map((id) => ({ id })) };
        if (failed > 0) setUploadWarning(true);
      }

      const initialCustomFields = buildInitialCustomFields(visibleCustomFields, cfValues, cfLabels);
      if (initialCustomFields.length > 0) payload.initial_custom_fields = initialCustomFields;

      if (capabilities.checklists) {
        const initialChecklists = meaningfulChecklists();
        if (initialChecklists.length > 0) payload.initial_checklists = initialChecklists;
      }

      if (capabilities.watchers && watchers.length > 0 && showsProperty('TASK_FORM_PROPERTY_WATCHERS')) {
        payload.initial_watchers = watchers.map((w) => ({
          entity: { id: w.id },
          display_name: w.name,
        }));
      }

      const created = await store.CreateTask(payload);
      const createdId = getEntityId(created);
      if (createdId && sendTags.length > 0) await assignTagsToEntity(sendTags, createdId, 'task');
      clearDraft(storageKey);
      onCreated?.((created ?? null) as Task | null, createdId);
    } catch {
      // The store surfaces the failure; the draft stays so nothing is lost.
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitEdit = async () => {
    if (!taskId || !baseline) return;
    setIsSubmitting(true);

    try {
      // Only what actually changed goes into the mask: a field sent back
      // unchanged is still a write, and writes overwrite whatever moved
      // underneath while this page was open.
      const paths: string[] = [];
      // A patch is a task with holes in it — the mask says which holes count.
      const patch: Partial<Task> = {};

      if (form.title.trim() !== baseline.form.title.trim()) {
        patch.title = form.title.trim();
        paths.push('title');
      }
      if (form.description.trim() !== baseline.form.description.trim()) {
        patch.description = form.description.trim();
        paths.push('description');
      }
      if (selectedTypeId !== baseline.typeId) {
        patch.type = taskTypeEnumForDefinition(selectedType);
        paths.push('type');
        // The registry id travels with the enum, or a custom type would
        // collapse to plain "task" on the way out.
        patch.task_type_id = taskTypeIdForRequest(selectedType);
        paths.push('task.task_type_id');
      }
      if (categoryId !== baseline.categoryId) {
        patch.category_id = categoryId;
        paths.push('category_id');
      }
      if (form.priority !== baseline.form.priority) {
        patch.priority = form.priority as Task['priority'];
        paths.push('task.priority');
      }
      if (form.assignee !== baseline.form.assignee) {
        // Clearing the assignee is an answer: the path travels with nothing
        // behind it, which is how the field is emptied.
        if (form.assignee) {
          patch.assignee = {
            // An id is the whole reference; the rest of the entity is the
            // server's to fill from it.
            entity: { id: form.assignee } as Entity,
            display_name: form.assigneeName || '',
          } as Actor;
        }
        paths.push('task.assignee');
      }
      if (form.due_date !== baseline.form.due_date) {
        if (form.due_date) patch.due_date = new Date(form.due_date).toISOString();
        paths.push('task.due_date');
      }
      if (form.start_date !== baseline.form.start_date) {
        if (form.start_date) patch.start_date = new Date(form.start_date).toISOString();
        paths.push('task.start_date');
      }
      if (capabilities.timeTracking && form.estimated_hours !== baseline.form.estimated_hours) {
        const hours = parseFloat(form.estimated_hours);
        if (form.estimated_hours && !Number.isNaN(hours) && hours > 0) {
          patch.estimated_duration = `${Math.round(hours * 3600)}s`;
        }
        paths.push('task.estimated_duration');
      }

      if (paths.length > 0) {
        await store.UpdateTask(taskId, {
          task: patch as Task,
          field_mask: paths,
        } as UpdateTaskRequest);
      }

      // Checklists here are new ones only; the existing ones are edited where
      // they are ticked off, on the task itself.
      if (capabilities.checklists) {
        for (const checklist of meaningfulChecklists()) {
          await store.AddChecklist(taskId, {
            task: { id: taskId } as Entity,
            checklist: checklist as unknown as TaskChecklist,
          });
        }
      }

      if (capabilities.watchers) {
        const current = watchers.map((w) => w.id);
        for (const id of current.filter((wid) => !baseline.watcherIds.includes(wid))) {
          await store.AddTaskWatcher(taskId, {
            task: { id: taskId } as Entity,
            watcher: { entity: { id } as Entity } as Actor,
          });
        }
        for (const id of baseline.watcherIds.filter((wid) => !current.includes(wid))) {
          await store.RemoveTaskWatcher(taskId, id);
        }
      }

      // Custom fields are stored one call at a time, and only the ones this
      // type asks for: a value the type no longer shows is left alone rather
      // than deleted behind the person's back.
      for (const def of visibleCustomFields) {
        const fieldId = customFieldId(def);
        const next = cfValues[fieldId];
        if (customFieldValuesEqual(next, baselineCf?.[fieldId])) continue;
        const value = buildCustomFieldValue(def, next, cfLabels[fieldId]);
        if (value) {
          await store.SetTaskCustomField(taskId, fieldId, { field_id: fieldId, value });
        } else {
          await store.RemoveTaskCustomField(taskId, fieldId);
        }
      }

      await syncTagsForEntity(baselineTags ?? [], tags, taskId, 'task');

      // What was saved is the new baseline — otherwise leaving the page right
      // after saving would still be challenged about unsaved changes.
      setBaseline({
        form,
        typeId: selectedTypeId,
        categoryId,
        watcherIds: watchers.map((w) => w.id),
      });
      setBaselineTags(tags);
      setBaselineCf(cfValues);
      setChecklists([]);
      onSaved?.(taskId);
    } catch {
      // The store surfaces the failure; nothing typed is thrown away.
    } finally {
      setIsSubmitting(false);
    }
  };

  const submit = async () => {
    if (!validate()) return;
    if (isEdit) await submitEdit();
    else await submitCreate();
  };

  const canSubmit = form.title.trim().length > 0 && !isSubmitting;

  // ⌘/Ctrl+Enter submits from anywhere in the form. Escape is deliberately not
  // bound here — the surface owns what Escape means for it.
  const submitRef = useRef(submit);
  submitRef.current = submit;
  const submitNow = useCallback(() => submitRef.current(), []);

  // ─── Rendering helpers ──────────────────────────────────────────────────
  // These read `t`, so they live here rather than being written twice.
  const errorText = useCallback(
    (field: string): string | undefined => {
      const error = errors[field];
      return error ? t(error.key, error.params) : undefined;
    },
    [errors, t],
  );

  const typeLabel = useCallback(
    (def: TaskTypeDefinition | null): string => {
      if (!def) return '';
      const key = typeDefLabelKey(def);
      return key ? t(key) : def.display_name || def.slug;
    },
    [t],
  );

  const projectLabel = (project: Project): string =>
    project.key ? `${project.key} — ${project.name}` : project.name || getEntityId(project);

  /** Only the parent kinds that can actually be picked. */
  const parentKinds = PARENT_KINDS.filter((kind) => {
    if (kind === 'project') return projects.length > 0 || parentKind === 'project';
    if (kind === 'team') return teams.length > 0 || parentKind === 'team';
    return true;
  });

  /** Options for the parent value picker, per selected parent type. */
  const parentOptions =
    parentKind === 'project'
      ? projects.map((project) => ({ value: getEntityId(project), label: projectLabel(project) }))
      : parentKind === 'team'
        ? teams.map((team) => ({ value: getEntityId(team), label: team.name || getEntityId(team) }))
        : [];

  const parentIsLoading =
    (parentKind === 'project' && projectStore.isLoading && projects.length === 0) ||
    (parentKind === 'team' && teamStore.isLoading && teams.length === 0);

  const parentLoadError =
    (parentKind === 'project' && !projectStore.isLoading && !!projectStore.error) ||
    (parentKind === 'team' && !teamStore.isLoading && !!teamStore.error);

  const retryParents = useCallback(() => {
    if (parentKind === 'project') loadProjects();
    else loadTeams();
  }, [parentKind, loadProjects, loadTeams]);

  /**
   * What the parent reads as. "No parent" is an answer, not an absence, so it
   * reads as a chosen value; a kind picked without an instance yet is the only
   * genuinely unset state here.
   */
  const parentValue =
    parentKind === 'none'
      ? t('task.createForm.parent.none')
      : parentKind === 'tenant'
        ? t('task.createForm.parent.tenant')
        : parentId
          ? (parentOptions.find((option) => option.value === parentId)?.label ??
            `${parentType || t('task.createForm.parent.entity')}: ${parentId.slice(0, 8)}`)
          : '';

  /** How the current parent reads in prose (template origins, popover hint). */
  const parentDisplayName = parentValue || t(`task.createForm.parent.${parentKind}`);

  const formatDate = useCallback((value: string): string => {
    if (!value) return '';
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }, []);

  const categoryValue = selectedCategory
    ? selectedCategory.code
      ? `${selectedCategory.code} — ${selectedCategory.name}`
      : selectedCategory.name
    : '';

  const tagsValue =
    tags.length === 0
      ? ''
      : tags.length <= 2
        ? tags.map((tag) => tag.name).join(', ')
        : t('task.createForm.tagCount', { count: tags.length });

  const priorityValue = t(getStatusMapping(taskPriorityMap, form.priority).labelKey);
  const priorityIcon = getPriorityIconPath(form.priority);
  const priorityIconClass = getPriorityColor(form.priority);

  /** The scope the people-pickers list from: this parent, when it is one. */
  const pickerScope: UserPickerScope | undefined = hasContainerParent
    ? { typeName: parentType, id: parentId, label: parentDisplayName }
    : undefined;

  // ─── Required work still outstanding ────────────────────────────────────
  // Said before the button is pressed, not after it is refused.
  const missingRequired: string[] = [];
  const namedMissing = new Set<string>();
  const noteMissing = (label: string) => {
    if (!label || namedMissing.has(label)) return;
    namedMissing.add(label);
    missingRequired.push(label);
  };
  if (!form.title.trim()) noteMissing(t('task.field.title'));
  if (capabilities.requiresAssignee && !form.assignee) {
    noteMissing(t('task.field.assignee'));
  }
  // `requires_assignee` and an assignee slot marked required say the same thing
  // twice; the set keeps the footer from saying it twice too.
  for (const missing of missingProperties) noteMissing(missing.label);
  for (const def of visibleCustomFields) {
    if (
      def.is_required &&
      def.field_type !== 'CUSTOM_FIELD_TYPE_CHECKBOX' &&
      isCustomFieldEmpty(cfValues[customFieldId(def)])
    ) {
      noteMissing(def.display_name);
    }
  }

  /**
   * Values this type's form has no row for, and so will not send.
   *
   * Switching type mid-compose is how these appear: the panel rebuilds, the row
   * goes away, and what was typed into it stays in the draft. Keeping it is the
   * point — flipping between two types to compare them must not cost the work,
   * and switching back brings every value straight back. What must not happen is
   * creating the task as if the value had been collected, so it is named here
   * and the payload drops it.
   */
  const hiddenDropped: string[] = [];
  if (formLayout.configured) {
    /** A value the person put there — a type default is not worth warning about. */
    const carriesTypedValue = (property: TaskFormProperty): boolean => {
      switch (property) {
        case 'TASK_FORM_PROPERTY_CATEGORY':
          return !!categoryId && touched.current.has('category');
        case 'TASK_FORM_PROPERTY_PRIORITY':
          return touched.current.has('priority');
        case 'TASK_FORM_PROPERTY_ASSIGNEE':
          return !!form.assignee && touched.current.has('assignee');
        case 'TASK_FORM_PROPERTY_DUE_DATE':
          return !!form.due_date;
        case 'TASK_FORM_PROPERTY_START_DATE':
          return !!form.start_date;
        case 'TASK_FORM_PROPERTY_ESTIMATE':
          return !!form.estimated_hours.trim() && touched.current.has('estimated_hours');
        case 'TASK_FORM_PROPERTY_TAGS':
          return tags.length > 0 && touched.current.has('tags');
        case 'TASK_FORM_PROPERTY_WATCHERS':
          return watchers.length > 0;
        // The parent is placement, not a panel value, and is sent either way.
        default:
          return false;
      }
    };
    for (const slot of formLayout.hidden) {
      if (slot.customFieldId) {
        const def = allCustomFields.find(
          (d) => customFieldId(d) === slot.customFieldId || d.slug === slot.customFieldId,
        );
        if (def && !isCustomFieldEmpty(cfValues[customFieldId(def)])) {
          hiddenDropped.push(def.display_name);
        }
        continue;
      }
      if (slot.property && carriesTypedValue(slot.property)) {
        hiddenDropped.push(t(formPropertyLabelKey(slot.property)));
      }
    }
  }

  /**
   * What the *selected type* does not ask for, while a value for it is on
   * screen. Changing the type rewrites the composition of the form, and
   * anything falling outside the new composition is not written — so it is
   * named before the button is pressed rather than discovered afterwards.
   */
  const typeDropped: string[] = [];
  if (!capabilities.timeTracking && form.estimated_hours) {
    typeDropped.push(t('task.estimatedHours'));
  }
  if (!capabilities.checklists && checklistItemCount > 0) typeDropped.push(t('task.checklists'));
  if (!capabilities.watchers && watchers.length > 0) typeDropped.push(t('task.watchers'));
  const visibleCustomFieldIds = new Set(visibleCustomFields.map(customFieldId));
  if (
    Object.entries(cfValues).some(
      ([fieldId, value]) => !isCustomFieldEmpty(value) && !visibleCustomFieldIds.has(fieldId),
    )
  ) {
    typeDropped.push(t('task.customFields'));
  }

  /**
   * What creating from a template cannot carry. `CreateTaskFromTemplate` takes
   * four overrides — title, parent, assignee, due date — so anything else set
   * here would be quietly lost. It is named instead.
   */
  const templateDropped: string[] = [];
  if (selectedTemplateId) {
    if (form.description.trim()) templateDropped.push(t('task.field.description'));
    if (touched.current.has('priority')) templateDropped.push(t('task.field.priority'));
    if (form.start_date) templateDropped.push(t('task.startDate'));
    if (form.estimated_hours) templateDropped.push(t('task.estimatedHours'));
    if (categoryId) templateDropped.push(t('task.categories.title'));
    if (checklistItemCount > 0) templateDropped.push(t('task.checklists'));
    if (watchers.length > 0) templateDropped.push(t('task.watchers'));
    if (pendingFiles.length > 0) templateDropped.push(t('task.attachments'));
    if (Object.values(cfValues).some((v) => !isCustomFieldEmpty(v))) {
      templateDropped.push(t('task.customFields'));
    }
  }

  return {
    // mode
    mode,
    isEdit,
    taskId,

    // stores + scope
    store,
    projectStore,
    teamStore,
    scope,
    customFieldsLoading: customFieldStore.isLoading,

    // the task being edited
    loadedTask,
    taskLoading,
    taskError,

    // refs
    descriptionRef,
    formRef,
    touched,
    titleFieldId,

    // parent
    parentKind,
    setParentKind,
    parentId,
    setParentId,
    otherTypeName,
    setOtherTypeName,
    parentType,
    setParent,
    parentKinds,
    parentOptions,
    parentIsLoading,
    parentLoadError,
    retryParents,
    parentValue,
    parentDisplayName,
    hasContainerParent,
    pickerScope,

    // form
    form,
    setForm,
    setField,
    setAssignee,
    errors,
    setErrors,
    errorText,
    isSubmitting,
    submit,
    submitNow,
    canSubmit,
    missingRequired,

    // type / category / policy
    availableTypes,
    selectedType,
    selectedTypeId,
    setSelectedTypeId,
    typeLabel,
    typeDropped,
    categories,
    categoryId,
    setCategoryId: setCategoryTouched,
    categoryValue,
    policy,
    capabilities,

    // form layout — what this type's form offers, in its order
    formLayout,
    formSlots,
    showsProperty,
    hidesProperty,
    hiddenDropped,

    // priority / tags / dates
    priorityValue,
    priorityIcon,
    priorityIconClass,
    tags,
    setTags: setTagsTouched,
    tagsValue,
    formatDate,

    // context
    context,
    contextLoading,
    contextError,
    retryContext,

    // templates
    templateEntries,
    selectedTemplateId,
    applyTemplate,
    templateDropped,

    // numbering
    numberingScheme,
    keyPreview,

    // custom fields
    visibleCustomFields,
    panelCustomFields,
    customFieldGroups,
    cfValues,
    cfLabels,
    setCustomField,
    filledInGroup,

    // secondary blocks
    checklists,
    setChecklists,
    checklistItemCount,
    watchers,
    setWatchers,
    pendingFiles,
    setPendingFiles,
    handleAddFiles,
    uploadWarning,

    // sections
    advancedPreset,
    sectionOpen,
    toggleSection,

    // description preview
    descriptionPreview,
    setDescriptionPreview,

    // unsaved work
    isDirty,
    draftRestored,
    setDraftRestored,
    discardDraft,
    flushDraft,
    storageKey,
  };
}

export type TaskFormHandle = ReturnType<typeof useTaskForm>;
